import { V2_FOOD_SOURCES } from "./v2Schema";
import { getSafeQuantityUnit, toFoodNumber } from "./v2FoodUtils";

const NUTRIENT_KEYS = Object.freeze(["potassium_mg", "phosphorus_mg", "sodium_mg", "protein_g"]);

export function validateLogQuantity(portions) {
  const parsed = Number(portions);

  if (!String(portions ?? "").trim() || !Number.isFinite(parsed) || parsed <= 0) {
    return {
      valid: false,
      error: "Portions eaten must be greater than 0.",
    };
  }

  return {
    valid: true,
    error: "",
    quantity: parsed,
    portions: parsed,
  };
}

export function getServingQuantity(food = {}) {
  const servingQuantity = toFoodNumber(food.quantity ?? food.serving_quantity);
  return servingQuantity > 0 ? servingQuantity : 1;
}

export function calculateLoggedNutrients(food = {}, portions = 0) {
  const multiplier = toFoodNumber(portions);

  return calculateNutrientsWithMultiplier(food, multiplier);
}

export function calculateNutrientsForQuantity(food = {}, quantity = 0) {
  const multiplier = toFoodNumber(quantity) / getServingQuantity(food);

  return calculateNutrientsWithMultiplier(food, multiplier);
}

function calculateNutrientsWithMultiplier(food = {}, multiplier = 0) {
  return {
    potassium_mg_snapshot: roundNutrient(toFoodNumber(food.potassium_mg) * multiplier),
    phosphorus_mg_snapshot: roundNutrient(toFoodNumber(food.phosphorus_mg) * multiplier),
    sodium_mg_snapshot: roundNutrient(toFoodNumber(food.sodium_mg) * multiplier),
    protein_g_snapshot: roundNutrient(toFoodNumber(food.protein_g) * multiplier),
  };
}

export function createFoodLogSnapshot(food, portions, { userId = "", date, now } = {}) {
  const validation = validateLogQuantity(portions);
  if (!validation.valid) {
    return { ok: false, errors: { quantity: validation.error }, log: null };
  }

  if (!food?.id) {
    return { ok: false, errors: { food: "Select a food first." }, log: null };
  }

  const createdAt = now || new Date().toISOString();
  const loggedForDate = date || formatLogDate(createdAt);
  const foodSource = food.type === "custom" ? V2_FOOD_SOURCES.custom : V2_FOOD_SOURCES.main;

  return {
    ok: true,
    errors: {},
    log: {
      user_id: userId,
      food_source: foodSource,
      food_id: food.id,
      food_name_snapshot: food.display_name || food.name_en || food.name || "",
      quantity: validation.portions,
      quantity_unit: "portion",
      portions: validation.portions,
      serving_quantity_snapshot: getServingQuantity(food),
      serving_unit_snapshot: getSafeQuantityUnit(food.quantity_unit || food.serving_unit, "serving"),
      ...calculateLoggedNutrients(food, validation.portions),
      logged_for_date: loggedForDate,
      created_at: createdAt,
    },
  };
}

export function getLogPortions(log = {}) {
  const explicitPortions = toFoodNumber(log.portions);
  if (explicitPortions > 0) return explicitPortions;

  const unit = String(log.quantity_unit || "").trim().toLowerCase();
  return unit === "portion" || unit === "portions" ? toFoodNumber(log.quantity) : 0;
}

export function getLogListedServing(log = {}) {
  const quantity = toFoodNumber(log.serving_quantity_snapshot);
  if (quantity <= 0) return null;

  return {
    quantity,
    unit: getSafeQuantityUnit(log.serving_unit_snapshot, "serving"),
  };
}

export function formatLogDate(value = new Date()) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatLogTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function groupLogsByDate(logs = []) {
  return logs.reduce((groups, log) => {
    const dateKey = log.logged_for_date || formatLogDate(log.created_at);
    if (!dateKey) return groups;

    groups[dateKey] = groups[dateKey] || [];
    groups[dateKey].push(log);
    return groups;
  }, {});
}

export function getLogsForDate(logs = [], dateKey = formatLogDate()) {
  return logs.filter((log) => (log.logged_for_date || formatLogDate(log.created_at)) === dateKey);
}

export function calculateDailyTotals(logs = []) {
  return logs.reduce(
    (totals, log) => {
      totals.potassium_mg += toFoodNumber(log.potassium_mg_snapshot);
      totals.phosphorus_mg += toFoodNumber(log.phosphorus_mg_snapshot);
      totals.sodium_mg += toFoodNumber(log.sodium_mg_snapshot);
      totals.protein_g += toFoodNumber(log.protein_g_snapshot);
      return totals;
    },
    {
      potassium_mg: 0,
      phosphorus_mg: 0,
      sodium_mg: 0,
      protein_g: 0,
    }
  );
}

export function sortLogsNewestFirst(logs = []) {
  return [...logs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

function roundNutrient(value) {
  return Math.round(value * 10) / 10;
}

export { NUTRIENT_KEYS };
